import React from "react";
import InfoIcon from "../../../assets/icons/info";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import { ReactComponent as SaveIcon } from "../../../assets/icons/saved.svg";
import { ReactComponent as SavingIcon } from "../../../assets/icons/saving.svg";
import Tabs from "./GOITabs";
import GOIButton from "./GOIButton";
import GOINestedTitlesWrapper from "./GOINestedTitles/GOINestedTitlesWrapper";
import useObjectInput from "../../../hooks/useObjectInput";
import "./index.css";

// Renders the header section of the generated object input
const GOIHeaderSection = ({
  saving, // to show saving status
  editMode, // to show editing status
  data, // object containing the data of the generated object
  handleSaveBtnClick, // function to handle save button click
  showSwitchView, // to show switch view button
  hideTitles, // in tree view we don't show the node title, so in that case it false
  activeTab, // contains active tab value
  setActiveTab, // function to set active tab
  backToEditingAllowed = true, // we don't want to show back to editing like if node is parent and has children
  cancelAllowed = true, // we don't want to show back to editing if the input value is null or empty by default
  handleValueChange, // whenever somone cancels the editing, we need to reset the value to the saved value
}) => {
  const parents = data.titleList || [];

  const knownField =
    [
      "File",
      "Integer",
      "Float",
      "String",
      "Time",
      "Date",
      "Boolean",
      "SingleChoice",
      "Timestamp",
    ].includes(data.class) || data.children;

  const {
    changeEditingChildrenIds,
    highlightData,
    uploadingData,
    treeViewIds,
    setTreeViewIds,
    isActiveObject,
    setActiveObjectId,
    defaultSavedValues,
  } = useObjectInput();

  const uploading = uploadingData[data.id] || {
    isFile: false,
    uploadProgress: 0,
    uploading: false,
    isSaved: false,
    verifying: false,
   }

  const changeView = () => {
    setTreeViewIds({ ...treeViewIds, [data.id]: !treeViewIds[data.id] });
  };

  const enableEditMode = () => {
    changeEditingChildrenIds(data.id, true);
  };

  const cancelEditMode = () => {
    changeEditingChildrenIds(data.id, false);
    handleValueChange({ target: { value: defaultSavedValues[data.id] } });
  };

  const renderEditModeStatus = () => {
    return (
      <>
        {saving ? (
          <>
            <SavingIcon />
            <p className="goi-header-section-saving">Saving...</p>
          </>
        ) : !editMode ? (
          <>
            <SaveIcon className="goi-header-section-saved-icon" />
            <p>Saved</p>
            {backToEditingAllowed && (
              <>
                |
                <p
                  className="goi-header-section-back-to-editing"
                  onClick={enableEditMode}
                >
                  Back to editing
                </p>
              </>
            )}
          </>
        ) : (
          <>
            <EditIcon />
            <p>Editing</p>
            {!(data.savedValue === null || !cancelAllowed) && (
              <>
                |{" "}
                <p
                  className="goi-header-section-cancel-editing"
                  onClick={cancelEditMode}
                >
                  Cancel Editing
                </p>
              </>
            )}
          </>
        )}
      </>
    );
  };

  const getHighlightStyle = () => {
    let styles = {};
    const isParentHighlighted = parents[0]?.id === highlightData.objectId;
    const isDependencyHighlighted = highlightData?.dependenciesId?.includes(
      parents[0]?.id
    );
    if (!isDependencyHighlighted && data.level > 1 && !isParentHighlighted) {
      styles = { color: "#fd8800" };
    } else if (data.level === 1 && isDependencyHighlighted) {
      styles = { color: "#fd8800", background: "#fac711" };
    } else if (isDependencyHighlighted) {
      styles = { background: "#fac711" };
    } else if (isParentHighlighted) {
      styles = { background: "red" };
    }
    return styles;
  };

  return (
    <div className={`goi-header-section-outer ${hideTitles ? "" : "px-10"}`}>
      {!hideTitles && (
        <div
          className="goi-header-section-name-wrapper"
          id="goi-header-section-name-wrapper"
        >
          {/* if node has no parent then only show the one title */}
          {parents.length === 1 ? (
            <>
              <span
                onClick={setActiveObjectId}
                id={parents[0].id}
                className={`${
                  isActiveObject(data.id) ? "" : "clickable"
                } title title-${parents[0].id}`}
                style={getHighlightStyle()}
              >
                {parents[0].name}
              </span>

              <div className="goi-tooltip">
                <InfoIcon />
                <span className="goi-tooltiptext">{data.attrDescription}</span>
              </div>
            </>
          ) : (
            // show all of parent titles if node has atleast one parent
            <GOINestedTitlesWrapper key={data.id} data={data} items={parents} />
          )}
        </div>
      )}

      <div
        className={`goi-header-section-editing-outer ${
          !hideTitles ? "pr-10" : "px-10"
        }`}
      >
        <div
          className={`editing-switch-wrapper ${
            showSwitchView ? "editing-switch-wrapper-switch" : ""
          }`}
        >
          <div className="goi-header-section-editing-wrapper">
            {renderEditModeStatus()}
          </div>
          {showSwitchView && (
            <div className="switch-button-wrapper">
              <p onClick={changeView} className="cp text-underline">
                Switch View
              </p>
            </div>
          )}
        </div>

        <div className="goi-header-section-right-wrapper">
          <Tabs activeTab={activeTab} setActiveTab={setActiveTab} data={data} />

          <div className="class-wrapper">
            <p className="class-property">
              {knownField ? data.class : "Unknown"}
            </p>
            <GOIButton
              disable={(data.class === "File" && uploading.verifying) || !knownField}
              editMode={editMode}
              handleSaveBtnClick={handleSaveBtnClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GOIHeaderSection;
